import { useState, useEffect } from 'react'
import api from '../../services/api'
import { toast } from 'react-hot-toast'
import UserDetailModal from './UserDetailModal'
import {
  MagnifyingGlassIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  AcademicCapIcon,
  CheckCircleIcon,
  XCircleIcon,
  FunnelIcon,
  TrashIcon,
  EyeIcon,
} from '@heroicons/react/24/outline'

export default function UserManagement() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState({})
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({
    role: '',
    is_active: '',
    is_enrolled: '',
  })
  const [sortBy, setSortBy] = useState('created_at')
  const [sortDir, setSortDir] = useState('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [showFilters, setShowFilters] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [showUserDetail, setShowUserDetail] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState([])

  useEffect(() => {
    fetchUsers()
  }, [search, filters, sortBy, sortDir, currentPage])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams({
        page: currentPage,
        sort_by: sortBy,
        sort_dir: sortDir,
      })

      if (search) params.append('search', search)
      if (filters.role) params.append('role', filters.role)
      if (filters.is_active) params.append('is_active', filters.is_active)
      if (filters.is_enrolled) params.append('is_enrolled', filters.is_enrolled)

      const response = await api.get(`/admin/users?${params}`)
      setUsers(response.data.data)
      setPagination({
        current_page: response.data.current_page,
        last_page: response.data.last_page,
        per_page: response.data.per_page,
        total: response.data.total,
      })
    } catch (error) {
      console.error('Error fetching users:', error)
      toast.error('Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  const handleRoleChange = async (userId, newRole) => {
    try {
      await api.put(`/admin/users/${userId}/role`, { role: newRole })
      toast.success('User role updated successfully')
      fetchUsers()
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update role'
      toast.error(message)
    }
  }

  const handleToggleStatus = async (userId) => {
    try {
      await api.put(`/admin/users/${userId}/toggle-status`)
      toast.success('User status updated successfully')
      fetchUsers()
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update status'
      toast.error(message)
    }
  }

  const handleDeleteUser = async (userId, userName) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete ${userName}?\n\n` +
      `This will permanently delete:\n` +
      `• All user data\n` +
      `• All progress and submissions\n` +
      `• All purchases\n` +
      `• All certificates\n\n` +
      `This action cannot be undone!`
    )
    
    if (!confirmed) return
    
    try {
      await api.delete(`/admin/users/${userId}`)
      toast.success(`User ${userName} deleted successfully`)
      fetchUsers()
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete user'
      toast.error(message)
    }
  }

  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) {
      toast.error('No users selected')
      return
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedUsers.length} user(s)?\n\n` +
      `This will permanently delete all their:\n` +
      `• User data\n` +
      `• Progress and submissions\n` +
      `• Purchases\n` +
      `• Certificates\n\n` +
      `This action cannot be undone!`
    )

    if (!confirmed) return

    try {
      await Promise.all(
        selectedUsers.map(userId => api.delete(`/admin/users/${userId}`))
      )
      toast.success(`${selectedUsers.length} user(s) deleted successfully`)
      setSelectedUsers([])
      fetchUsers()
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete users'
      toast.error(message)
    }
  }

  const toggleSelectUser = (userId) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    )
  }

  const toggleSelectAll = () => {
    if (selectedUsers.length === users.filter(u => u.role !== 'admin').length) {
      setSelectedUsers([])
    } else {
      setSelectedUsers(users.filter(u => u.role !== 'admin').map(u => u.id))
    }
  }

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-100 text-purple-800 border-purple-200'
      case 'instructor':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'student':
        return 'bg-green-100 text-green-800 border-green-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <ShieldCheckIcon className="w-4 h-4" />
      case 'instructor':
        return <AcademicCapIcon className="w-4 h-4" />
      case 'student':
        return <UserCircleIcon className="w-4 h-4" />
      default:
        return <UserCircleIcon className="w-4 h-4" />
    }
  }

  const clearFilters = () => {
    setFilters({
      role: '',
      is_active: '',
      is_enrolled: '',
    })
    setSearch('')
    setCurrentPage(1)
  }

  const activeFilterCount = Object.values(filters).filter(Boolean).length + (search ? 1 : 0)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
          <p className="text-gray-600 mt-1">Manage user roles, permissions, and access</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setCurrentPage(1)
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors relative"
          >
            <FunnelIcon className="w-5 h-5" />
            <span className="font-medium">Filters</span>
            {activeFilterCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                value={filters.role}
                onChange={(e) => {
                  setFilters({ ...filters, role: e.target.value })
                  setCurrentPage(1)
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Roles</option>
                <option value="student">Students</option>
                <option value="instructor">Instructors</option>
                <option value="admin">Admins</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={filters.is_active}
                onChange={(e) => {
                  setFilters({ ...filters, is_active: e.target.value })
                  setCurrentPage(1)
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Enrollment</label>
              <select
                value={filters.is_enrolled}
                onChange={(e) => {
                  setFilters({ ...filters, is_enrolled: e.target.value })
                  setCurrentPage(1)
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Users</option>
                <option value="true">Enrolled</option>
                <option value="false">Not Enrolled</option>
              </select>
            </div>

            {activeFilterCount > 0 && (
              <div className="sm:col-span-3 flex justify-end">
                <button
                  onClick={clearFilters}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bulk Actions */}
      {selectedUsers.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
          <span className="text-sm font-medium text-blue-900">
            {selectedUsers.length} user(s) selected
          </span>
          <button
            onClick={handleBulkDelete}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <TrashIcon className="w-4 h-4" />
            Delete Selected
          </button>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12">
            <UserCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
            <p className="mt-1 text-sm text-gray-500">Try adjusting your search or filters</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left w-12">
                      <input
                        type="checkbox"
                        checked={selectedUsers.length === users.filter(u => u.role !== 'admin').length && users.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                      Role
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      Status
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                      Progress
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden xl:table-cell">
                      Purchases
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden xl:table-cell">
                      Joined
                    </th>
                    <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-3 py-4">
                        {user.role !== 'admin' && (
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(user.id)}
                            onChange={() => toggleSelectUser(user.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        )}
                      </td>
                      <td className="px-3 py-4">
                        <div className="flex items-center min-w-0">
                          <div className="flex-shrink-0 h-8 w-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                            {user.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="ml-3 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{user.name}</div>
                            <div className="text-xs text-gray-500 truncate">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-4 hidden sm:table-cell">
                        <select
                          value={user.role}
                          onChange={(e) => handleRoleChange(user.id, e.target.value)}
                          className={`text-xs font-semibold px-2 py-1 rounded-full border ${getRoleBadgeColor(
                            user.role
                          )}`}
                        >
                          <option value="student">Student</option>
                          <option value="instructor">Instructor</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td className="px-3 py-4 hidden md:table-cell">
                        <button
                          onClick={() => handleToggleStatus(user.id)}
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                            user.is_active
                              ? 'bg-green-100 text-green-800 hover:bg-green-200'
                              : 'bg-red-100 text-red-800 hover:bg-red-200'
                          } transition-colors`}
                        >
                          {user.is_active ? (
                            <>
                              <CheckCircleIcon className="w-3 h-3" />
                              Active
                            </>
                          ) : (
                            <>
                              <XCircleIcon className="w-3 h-3" />
                              Inactive
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-900 hidden lg:table-cell">
                        {user.completed_count > 0 ? (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[80px]">
                              <div
                                className="bg-blue-600 h-2 rounded-full"
                                style={{
                                  width: `${Math.min(
                                    (user.completed_count / user.progress_count) * 100,
                                    100
                                  )}%`,
                                }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              {user.completed_count}/{user.progress_count}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">No activity</span>
                        )}
                      </td>
                      <td className="px-3 py-4 text-sm text-gray-900 hidden xl:table-cell">
                        {user.purchase_count > 0 ? (
                          <div>
                            <div className="font-medium">${user.total_spent.toFixed(2)}</div>
                            <div className="text-xs text-gray-500">
                              {user.purchase_count} {user.purchase_count === 1 ? 'purchase' : 'purchases'}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">No purchases</span>
                        )}
                      </td>
                      <td className="px-3 py-4 text-xs text-gray-500 hidden xl:table-cell whitespace-nowrap">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-4 text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            className="text-blue-600 hover:text-blue-900 transition-colors p-1 hover:bg-blue-50 rounded"
                            onClick={() => {
                              setSelectedUser(user)
                              setShowUserDetail(true)
                            }}
                            title="View details"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                          {user.role !== 'admin' && (
                            <button
                              className="text-red-600 hover:text-red-900 transition-colors p-1 hover:bg-red-50 rounded"
                              onClick={() => handleDeleteUser(user.id, user.name)}
                              title="Delete user"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.last_page > 1 && (
              <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
                <div className="text-sm text-gray-700">
                  Showing{' '}
                  <span className="font-medium">
                    {(pagination.current_page - 1) * pagination.per_page + 1}
                  </span>{' '}
                  to{' '}
                  <span className="font-medium">
                    {Math.min(pagination.current_page * pagination.per_page, pagination.total)}
                  </span>{' '}
                  of <span className="font-medium">{pagination.total}</span> users
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(pagination.last_page, p + 1))}
                    disabled={currentPage === pagination.last_page}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-sm text-gray-600 mb-1">Total Users</div>
          <div className="text-2xl font-bold text-gray-900">{pagination.total || 0}</div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-sm text-gray-600 mb-1">Students</div>
          <div className="text-2xl font-bold text-green-600">
            {users.filter((u) => u.role === 'student').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-sm text-gray-600 mb-1">Instructors</div>
          <div className="text-2xl font-bold text-blue-600">
            {users.filter((u) => u.role === 'instructor').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="text-sm text-gray-600 mb-1">Admins</div>
          <div className="text-2xl font-bold text-purple-600">
            {users.filter((u) => u.role === 'admin').length}
          </div>
        </div>
      </div>

      {/* User Detail Modal */}
      <UserDetailModal
        user={selectedUser}
        isOpen={showUserDetail}
        onClose={() => {
          setShowUserDetail(false)
          setSelectedUser(null)
        }}
      />
    </div>
  )
}
